import {
	CSSProperties,
	RefObject,
	useLayoutEffect,
	useMemo,
	useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

import { LogoWordmark, QLogo } from '@quizzach/assets';

export const seeded = (seed: number) => {
	let x = seed;
	return () => (x = (x * 9301 + 49297) % 233280) / 233280;
};

const BURST_RISE_FRAMES = 12;
const BURST_FALL_FRAMES = 40;

/**
 * One piece's flight out of the popper at the bottom centre of the hero: air
 * drag brakes the launch, then it falls at its own speed, swaying and tumbling
 * like paper, until it drops out through the bottom of the hero.
 */
const burstFrames = (
	piece: HTMLElement,
	width: number,
	height: number,
	r: () => number
) => {
	const restX = piece.offsetLeft;
	const restY = piece.offsetTop;
	const startX = width / 2;
	const startY = height + 10;
	const endY = height + 40;
	const peakY = -50 + r() * height * 0.6;
	const peakX = startX + (restX - startX) * (0.72 + r() * 0.34);
	const riseTime = 0.45 + r() * 0.25;
	const fallSpeed = 120 + r() * 100;
	const fallTime = (endY - peakY) / fallSpeed;
	const total = riseTime + fallTime;

	const direction = r() < 0.5 ? -1 : 1;
	const launchSpin = 420 + r() * 620;
	const tumbleSpin = 40 + r() * 80;
	const flipRate = 200 + r() * 320;
	const flipAxis = (r() * 1.4 - 0.7).toFixed(2);
	const swayWidth = (8 + r() * 20) * Math.min(1, width / 800 + 0.35);
	const swayRate = 0.6 + r() * 0.6;
	const swayPhase = r() * Math.PI * 2;

	const frame = (
		t: number,
		x: number,
		y: number,
		tilt: number,
		size: number
	) => {
		const progress = t / total;
		const braking = 1 - Math.exp(-3 * progress);
		const spin = launchSpin * braking + tumbleSpin * t;
		return {
			offset: progress,
			opacity: Math.min(1, t / 0.04),
			translate: `${(x - restX).toFixed(1)}px ${(y - restY).toFixed(1)}px`,
			transform:
				`rotate(${(direction * spin + tilt).toFixed(1)}deg) ` +
				`rotate3d(1, ${flipAxis}, 0, ${(flipRate * t).toFixed(1)}deg) ` +
				`scale(${size.toFixed(2)})`,
		};
	};

	const frames = [];
	for (let i = 0; i <= BURST_RISE_FRAMES; i++) {
		const u = i / BURST_RISE_FRAMES;
		const braked = (1 - Math.exp(-4.2 * u)) / (1 - Math.exp(-4.2));
		frames.push(
			frame(
				u * riseTime,
				startX + (peakX - startX) * braked,
				startY + (peakY - startY) * braked,
				0,
				0.55 + 0.45 * Math.min(1, u * 3)
			)
		);
	}
	for (let i = 1; i <= BURST_FALL_FRAMES; i++) {
		const s = i / BURST_FALL_FRAMES;
		const t = s * fallTime;
		// gathers speed from the peak, then falls steadily with a growing sway
		const fallen = s < 0.15 ? 3.604 * s * s : 0.0811 + 1.0811 * (s - 0.15);
		const fade = Math.min(1, s / 0.25);
		const sway = Math.sin(Math.PI * 2 * swayRate * t + swayPhase);
		frames.push(
			frame(
				riseTime + t,
				peakX + swayWidth * sway * fade,
				peakY + (endY - peakY) * fallen + swayWidth * 0.2 * sway * sway * fade,
				16 * sway * fade,
				1
			)
		);
	}
	return { frames, total };
};

export const Confetti = ({
	count,
	seed,
	logos = 0,
	burst,
}: {
	count: number;
	seed: number;
	/** Q logos among the pieces, as in the voucher activation animation. */
	logos?: number;
	burst?: boolean;
}) => {
	const pieceRefs = useRef<(HTMLElement | null)[]>([]);
	const pieces = useMemo(() => {
		const r = seeded(seed);
		const colors = ['var(--teal)', 'var(--coral)', 'var(--gold)'];
		return Array.from({ length: count + logos }, (_, i) => {
			const logo = i >= count;
			// with reduced motion the pieces stay put, so logos sit nearly upright
			const turn = Math.round(logo ? r() * 50 - 25 : r() * 180);
			return {
				logo,
				style: {
					left: `${(r() * 100).toFixed(1)}%`,
					top: `${(r() * 100).toFixed(1)}%`,
					...(logo
						? {
								color: colors[i % 3],
								'--size': `${Math.round(15 + r() * 15)}px`,
							}
						: { background: colors[i % 3] }),
					'--turn': `${turn}deg`,
				} as unknown as CSSProperties,
			};
		});
	}, [count, logos, seed]);

	useLayoutEffect(() => {
		if (!burst) return;
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
		const r = seeded(seed + 1);
		const animations = pieceRefs.current.flatMap((piece) => {
			const hero = piece?.parentElement;
			if (!piece || !hero) return [];
			const { frames, total } = burstFrames(
				piece,
				hero.offsetWidth,
				hero.offsetHeight,
				r
			);
			// stays below the hero's bottom edge, clipped, once it has fallen out
			return [
				piece.animate(frames, {
					duration: total * 1000,
					delay: r() * 120,
					easing: 'linear',
					fill: 'both',
				}),
			];
		});
		return () => animations.forEach((animation) => animation.cancel());
	}, [burst, pieces, seed]);

	return (
		<>
			{pieces.map(({ style, logo }, i) => (
				<i
					key={i}
					ref={(el) => {
						pieceRefs.current[i] = el;
					}}
					className={logo ? 'cf logo' : 'cf'}
					style={style}
				>
					{logo && <QLogo />}
				</i>
			))}
		</>
	);
};

export const HeroVoucher = ({ confetti }: { confetti?: boolean }) => {
	const { t } = useTranslation();

	return (
		<span className='vc'>
			{confetti && <Confetti count={16} seed={7} />}
			<LogoWordmark width={74} height={23} />
			<b>
				{t('voucherShop.hero.cardLine1')}
				<br />
				{t('voucherShop.hero.cardLine2')}
			</b>
			<small>{t('voucherShop.hero.cardHand')}</small>
		</span>
	);
};

/** The stack of gift cards; a single card on phones. */
export const GiftCardStack = ({
	phone,
	cardsRef,
}: {
	phone: boolean;
	cardsRef?: RefObject<HTMLDivElement>;
}) =>
	phone ? (
		<div className='one' ref={cardsRef}>
			<HeroVoucher confetti />
		</div>
	) : (
		<div className='stack' ref={cardsRef}>
			<HeroVoucher />
			<HeroVoucher />
			<HeroVoucher confetti />
		</div>
	);
